import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Activity, MessageSquare, ArrowRight, Sparkles, Megaphone, X,
    Shield, Zap, Target, Wifi, Cpu, Briefcase, Layers, MapPin, Search, PlusCircle, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { useUnreadAnnouncements } from '../../hooks/useUnreadAnnouncements';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';
import { getStaffDisplayName } from '../../utils/staffDisplay';

const GlassPanel = ({ children, className = "", onClick }) => (
    <div
        onClick={onClick}
        className={`bg-black/20 backdrop-blur-2xl border border-white/10 rounded-3xl overflow-hidden relative group transition-all duration-500 hover:bg-black/40 hover:border-white/20 shadow-2xl ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''} ${className}`}
    >
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        <div className="relative z-10 h-full">{children}</div>
    </div>
);

const ActionWidget = ({ icon: Icon, title, count, subtitle, gradient, onClick, delay = 0 }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.5 }} className="h-full">
        <GlassPanel onClick={onClick} className="h-full p-6 flex flex-col justify-between">
            <div className={`absolute -right-12 -top-12 w-40 h-40 bg-gradient-to-br ${gradient} opacity-20 blur-3xl rounded-full group-hover:opacity-40 transition-opacity`} />
            <div className="flex justify-between items-start mb-6">
                <div className={`p-4 rounded-2xl bg-gradient-to-br ${gradient} border border-white/20 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
                {count !== undefined && (
                    <span className="text-3xl font-black text-white drop-shadow-md">{count}</span>
                )}
            </div>
            <div>
                <h3 className="text-xl font-bold text-white tracking-wide mb-1">{title}</h3>
                <p className="text-sm text-gray-400 font-medium">{subtitle}</p>
            </div>
        </GlassPanel>
    </motion.div>
);

const StaffDashboard = () => {
    const { user } = useAuth();
    const { tickets } = useTickets();
    const navigate = useNavigate();
    const { hasUnread } = useUnreadAnnouncements();
    const dynamicGradient = useDynamicGradient();
    const [currentTime, setCurrentTime] = useState(new Date());

    const [inProgressTickets, setInProgressTickets] = useState([]);
    const [escalatedTickets, setEscalatedTickets] = useState([]);
    const [cityTickets, setCityTickets] = useState([]);
    const [resolvedTicketsToday, setResolvedTicketsToday] = useState(0);
    const [dismissedNotification, setDismissedNotification] = useState(false);
    const [systemPulse, setSystemPulse] = useState(true);

    const dailyTip = useMemo(() => {
        const tips = [
            "A empatia é a chave para um bom atendimento. Tente se colocar no lugar do jogador.",
            "Antes de escalar um ticket, certifique-se de que esgotou todas as opções de resolução.",
            "Sempre verifique o histórico de punições do usuário antes de aplicar uma nova sanção.",
            "Um bom atendimento começa com um 'Olá' amigável e uma leitura atenta do problema.",
            "Lembre-se: Você é o rosto do Sistema Capital. Represente a comunidade com orgulho!",
            "Evite respostas muito curtas (sim/não). Explique o contexto para evitar retornos.",
            "Sempre utilize seu codinome de Staff para garantir sua privacidade e padronização.",
            "Seja claro e objetivo, mas sem perder a cordialidade. O jogador precisa entender a resolução.",
            "Em casos de denúncias, analise as evidências com imparcialidade antes de tomar qualquer decisão.",
            "Não alimente discussões com jogadores estressados. Mantenha a postura profissional e neutra.",
            "Utilize as respostas prontas apenas como base. Personalize sempre que possível.",
            "Se estiver com dúvidas sobre uma regra, consulte o manual ou pergunte a um superior antes de responder.",
            "Um ticket resolvido rapidamente é ótimo, mas um ticket resolvido corretamente é excelente.",
            "Sempre revise sua ortografia antes de enviar uma resposta. A comunicação clara evita mal-entendidos.",
            "Atenção aos detalhes: muitas vezes a solução do problema está nas entrelinhas do que o jogador relatou.",
            "Trate todos os jogadores com o mesmo nível de respeito, independentemente da infração cometida.",
            "Mantenha a calma. Lidar com o público exige muita paciência e resiliência.",
            "No Sistema Capital, nós trabalhamos em equipe. Não tenha medo de pedir ajuda quando necessário.",
            "Sua saúde mental importa. Faça pausas, beba água e não leve problemas do jogo para a vida pessoal.",
            "Sempre confirme as informações no banco de dados e nos logs antes de validar uma solicitação.",
            "Agradeça ao jogador pelo contato, mesmo que a resposta final não seja a que ele esperava.",
            "Evite fazer promessas que não pode cumprir. Seja transparente sobre prazos e possibilidades.",
            "O respeito gera respeito. Um atendimento respeitoso pode desarmar um jogador frustrado.",
            "Conheça o servidor profundamente. Quanto mais você souber sobre as mecânicas, melhor será seu suporte.",
            "Se um jogador estiver enviando mensagens excessivas, peça educadamente que ele aguarde a análise.",
            "Verifique sempre se a categoria do ticket está correta e instrua o jogador se necessário.",
            "Use a formatação do texto para destacar informações e facilitar a leitura da resposta.",
            "Mantenha o histórico do ticket limpo e organizado. Deixe notas internas detalhadas se for escalar o caso.",
            "Sua conduta pode ser a diferença entre um jogador abandonar ou continuar no projeto. Faça valer a pena.",
            "Sempre forneça uma justificativa clara, plausível e baseada nas regras ao fechar um ticket.",
            "O papel primário da Staff é orientar. A punição deve ser o último recurso de correção.",
            "Mantenha-se sempre atualizado com os Comunicados Oficiais para não repassar informações defasadas.",
            "Nunca compartilhe informações internas da Staff ou dados de outros jogadores com terceiros.",
            "Seja paciente com os novatos. Todos nós já começamos do zero alguma vez e precisaram de ajuda.",
            "Compreenda que bugs acontecem. Informe ao jogador que a equipe responsável será notificada.",
            "A imparcialidade é inegociável. Não conceda privilégios a amigos conhecidos dentro do servidor.",
            "O bom senso é a sua maior ferramenta de trabalho. Use-o sempre!"
        ];
        return tips[Math.floor(Math.random() * tips.length)];
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
            setSystemPulse(prev => !prev);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!tickets || !user) return;

        // Meus Tickets: Responsabilidade do staff, marcados como NÃO finalizados ou NÃO resolvidos
        setInProgressTickets(tickets.filter(t =>
            t.assignedTo === user.id &&
            !['resolvido', 'fechado'].includes(t.status)
        ));

        // Escalações: Criadas pelo staff ou atribuídas a ele, não resolvido ou fechado
        setEscalatedTickets(tickets.filter(t =>
            (t.escalatedBy === user.id || t.assignedTo === user.id) &&
            t.isEscalated === true &&
            !['resolvido', 'fechado'].includes(t.status)
        ));

        // Fila da Cidade (New Tickets): Sem claim, da cidade dele ou tudo se for dev/admin
        setCityTickets(tickets.filter(t =>
            !t.assignedTo &&
            ['aberto', 'reaberto'].includes(t.status) &&
            t.type !== 'escalation' &&
            (!t.responses || !t.responses.some(r => r?.isStaff)) &&
            (user.accessLevel === 'admin' || user.accessLevel === 'direcao' || t.city === user.city)
        ));

        // Resolvidos Hoje (Total da equipe)
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);

        setResolvedTicketsToday(tickets.filter(t =>
            ['resolvido', 'fechado'].includes(t.status) &&
            new Date(t.last_update || t.updated_at || t.created_at) >= startOfDay
        ).length);

    }, [tickets, user]);

    const greeting = useMemo(() => {
        const hour = currentTime.getHours();
        const time = hour + currentTime.getMinutes() / 60;

        if (time < 5) return { text: 'BOA MADRUGADA', emoji: '🌑', sub: 'Monitoramento Noturno Ativo' };
        if (time < 12) return { text: 'BOM DIA', emoji: '☀️', sub: 'Sistemas Operacionais Iniciados' };
        if (time < 18) return { text: 'BOA TARDE', emoji: '🌤️', sub: 'Fluxo Intenso Detectado' };
        return { text: 'BOA NOITE', emoji: '🌙', sub: 'Vigilância Contínua' };
    }, [currentTime]);

    const cityStats = useMemo(() => {
        if (!tickets) return [];
        const counts = tickets.filter(t => !['resolvido', 'fechado'].includes(t.status)).reduce((acc, t) => {
            const cityName = t.city || 'Desconhecida';
            acc[cityName] = (acc[cityName] || 0) + 1;
            return acc;
        }, {});

        const colors = ['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-emerald-500', 'bg-amber-500'];
        return Object.entries(counts).map(([name, value], i) => ({
            name, value, color: colors[i % colors.length]
        })).sort((a, b) => b.value - a.value).slice(0, 5);
    }, [tickets]);

    return (
        <div className="relative min-h-[calc(100vh-4rem)] p-4 lg:p-8 font-sans selection:bg-blue-500/30">
            <div className="relative z-10 max-w-7xl mx-auto space-y-8">
                {/* DICA DE ATENDIMENTO */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <div className="relative group p-[1px] rounded-3xl overflow-hidden mb-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-60 group-hover:opacity-100 transition-opacity duration-1000" />
                        <div className="relative bg-black/80 backdrop-blur-2xl p-6 lg:p-8 rounded-3xl flex items-center gap-6">
                            <div className="bg-white/10 p-4 rounded-2xl">
                                <Sparkles className="w-8 h-8 text-pink-400 group-hover:animate-pulse" />
                            </div>
                            <div>
                                <h3 className="text-xs text-white/50 uppercase tracking-widest font-bold mb-2 flex items-center gap-2">
                                    <Shield className="w-4 h-4" /> Dica de Atendimento
                                </h3>
                                <p className="text-xl lg:text-2xl text-white font-light tracking-wide italic">
                                    "{dailyTip}"
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* HERO SECTION */}
                <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="w-full">
                    <GlassPanel className="p-8 lg:p-12 border-t border-t-white/20">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-4 py-1.5 uppercase font-bold tracking-widest text-xs">
                                        <span className={`w-2 h-2 rounded-full bg-emerald-400 mr-2 inline-block ${systemPulse ? 'opacity-100' : 'opacity-40'} transition-opacity`} />
                                        Sistema Conectado
                                    </Badge>
                                </div>
                                <h1 className="flex flex-wrap items-center gap-3 text-4xl lg:text-6xl font-black text-white uppercase tracking-tighter mb-2">
                                    <span className={`bg-gradient-to-r ${dynamicGradient} bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(255,255,255,0.1)]`}>
                                        {getStaffDisplayName(user)}
                                    </span>
                                    {user?.accessLevel && (
                                        <span className="text-2xl lg:text-4xl text-white/40 font-bold tracking-widest flex items-center">
                                            <span className="mr-3">-</span>
                                            {user.accessLevel.replace(/_/g, ' ')}
                                        </span>
                                    )}
                                </h1>
                                <p className="text-xl text-gray-400 font-medium flex items-center gap-3">
                                    {greeting.text} {greeting.emoji} <span className="text-white/20">•</span>
                                    <span className="text-blue-400 flex items-center gap-2">
                                        <Activity className="w-5 h-5" /> {greeting.sub}
                                    </span>
                                </p>
                            </div>

                            <div className="flex flex-col items-start lg:items-end">
                                <div className="text-6xl lg:text-8xl font-black font-mono tracking-tighter text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.2)]">
                                    {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                <div className="text-sm text-blue-400 font-mono uppercase tracking-widest mt-2 flex items-center gap-2">
                                    <Cpu className="w-4 h-4" />
                                    {new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }).format(currentTime)}
                                </div>
                            </div>
                        </div>
                    </GlassPanel>
                </motion.div>

                {/* NOTIFICATIONS */}
                <AnimatePresence>
                    {hasUnread && !dismissedNotification && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                            <GlassPanel className="p-6 border-purple-500/30! bg-purple-900/10!">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-purple-500/20 rounded-xl relative">
                                            <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full animate-ping" />
                                            <Megaphone className="text-purple-400 w-6 h-6" />
                                        </div>
                                        <div>
                                            <h4 className="text-white font-bold text-lg">Comunicados Oficiais</h4>
                                            <p className="text-purple-300/80 text-sm">Você tem novas mensagens não lidas da direção.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Button onClick={() => navigate('/staff/announcements')} className="bg-purple-600 hover:bg-purple-500 text-white rounded-xl">
                                            Ler Avisos
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => setDismissedNotification(true)} className="text-white/50 hover:text-white">
                                            <X className="w-5 h-5" />
                                        </Button>
                                    </div>
                                </div>
                            </GlassPanel>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* MAIN GRID */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* WIDGETS */}
                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <ActionWidget
                            icon={MessageSquare} title="Meus Tickets" count={inProgressTickets.length} subtitle="Tickets sob sua responsabilidade"
                            gradient="from-blue-600 to-indigo-600" onClick={() => navigate('/staff/your-tickets')} delay={0.3}
                        />
                        <ActionWidget
                            icon={Layers} title="Fila da Cidade" count={cityTickets.length} subtitle="Aguardando atendimento"
                            gradient="from-emerald-600 to-teal-600" onClick={() => navigate('/staff/new-tickets')} delay={0.4}
                        />
                        <ActionWidget
                            icon={Target} title="Escalações" count={escalatedTickets.length} subtitle="Casos de alta complexidade"
                            gradient="from-orange-600 to-red-600" onClick={() => navigate('/staff/escalations')} delay={0.5}
                        />
                        <ActionWidget
                            icon={CheckCircle} title="Resolvidos" count={resolvedTicketsToday} subtitle="Casos resolvidos hoje pela equipe"
                            gradient="from-purple-600 to-pink-600" delay={0.6}
                        />
                    </div>

                    {/* SIDEPANEL */}
                    <div className="space-y-6">
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="h-full">
                            <GlassPanel className="p-6 h-full flex flex-col">
                                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                                    <Zap className="w-5 h-5 text-yellow-400" /> Ações Rápidas
                                </h3>
                                <div className="space-y-3 flex-1">
                                    <Button onClick={() => navigate('/staff/new-tickets')} variant="outline" className="w-full justify-start h-14 bg-white/5 border-white/10 hover:bg-white/10 text-white rounded-xl">
                                        <Layers className="w-5 h-5 mr-3 text-gray-400" /> Novos Tickets
                                    </Button>
                                    <Button onClick={() => navigate('/staff/tools')} variant="outline" className="w-full justify-start h-14 bg-white/5 border-white/10 hover:bg-white/10 text-white rounded-xl">
                                        <Briefcase className="w-5 h-5 mr-3 text-gray-400" /> Ferramentas Staff
                                    </Button>
                                    <Button onClick={() => navigate('/staff/escalations/create')} variant="outline" className="w-full justify-start h-14 bg-white/5 border-white/10 hover:bg-white/10 text-white rounded-xl">
                                        <PlusCircle className="w-5 h-5 mr-3 text-gray-400" /> Nova Escalação
                                    </Button>
                                </div>

                                <div className="mt-8">
                                    <h3 className="text-sm font-bold text-white/50 uppercase tracking-widest mb-4">Volume por Cidade</h3>
                                    {cityStats.length > 0 ? (
                                        <div className="space-y-3">
                                            {cityStats.map((stat, idx) => (
                                                <div key={idx} className="space-y-1">
                                                    <div className="flex justify-between text-xs font-medium text-gray-300">
                                                        <span>{stat.name}</span>
                                                        <span>{stat.value}</span>
                                                    </div>
                                                    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                                                        <div className={`h-full ${stat.color} rounded-full`} style={{ width: `${Math.min((stat.value / tickets.length) * 100, 100)}%` }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500">Nenhum ticket aberto no momento.</p>
                                    )}
                                </div>
                            </GlassPanel>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StaffDashboard;
